import axios from 'axios';
import type {
  Movie,
  TvShow,
  MediaResponse,
  SearchResult,
  Season,
  Genre,
  NextReleaseInfo
} from '../types';

const API_KEY = import.meta.env.VITE_TMDB_API_KEY;
const BASE_URL = import.meta.env.VITE_TMDB_BASE_URL || 'https://api.themoviedb.org/3';
export const IMAGE_BASE = import.meta.env.VITE_TMDB_IMAGE_BASE || 'https://image.tmdb.org/t/p';

// Validate API key on startup
if (!API_KEY) {
  console.error(
    '%c TMDB API Key Missing! ',
    'background: #e50914; color: white; font-size: 14px; padding: 4px 8px; border-radius: 4px;',
    '\n\nTo fix:\n1. Get a free API key at https://www.themoviedb.org/settings/api\n2. Add VITE_TMDB_API_KEY=your_key to .env\n3. Restart the dev server'
  );
}

const api = axios.create({
  baseURL: BASE_URL,
  params: {
    api_key: API_KEY,
  },
});

// Image URL helpers
export const getImageUrl = (path: string | null, size: string = 'w500'): string => {
  if (!path) return '/placeholder-poster.svg';
  return `${IMAGE_BASE}/${size}${path}`;
};

export const getBackdropUrl = (path: string | null, size: string = 'original'): string => {
  if (!path) return '/placeholder-backdrop.svg';
  return `${IMAGE_BASE}/${size}${path}`;
};

// Network logo helper
export const getNetworkLogoUrl = (path: string | null, size: string = 'w300'): string => {
  if (!path) return '';
  return `${IMAGE_BASE}/${size}${path}`;
};

// Fetch network logos from TMDB
export const getNetworkLogos = async (networkId: number): Promise<string | null> => {
  try {
    const { data } = await api.get(`/network/${networkId}/images`);
    // Return the first logo path if available
    if (data.logos && data.logos.length > 0) {
      return data.logos[0].file_path;
    }
    return null;
  } catch {
    return null;
  }
};

// Trending
export const getTrending = async (
  mediaType: 'all' | 'movie' | 'tv' = 'all',
  timeWindow: 'day' | 'week' = 'week'
): Promise<MediaResponse<Movie | TvShow>> => {
  const { data } = await api.get(`/trending/${mediaType}/${timeWindow}`);
  return data;
};

// Popular
export const getPopular = async (
  type: 'movie' | 'tv',
  page: number = 1
): Promise<MediaResponse<Movie | TvShow>> => {
  const { data } = await api.get(`/${type}/popular`, { params: { page } });
  return data;
};

// Top Rated
export const getTopRated = async (
  type: 'movie' | 'tv',
  page: number = 1
): Promise<MediaResponse<Movie | TvShow>> => {
  const { data } = await api.get(`/${type}/top_rated`, { params: { page } });
  return data;
};

// Now Playing / On Air
export const getNowPlaying = async (type: 'movie' | 'tv'): Promise<MediaResponse<Movie | TvShow>> => {
  const endpoint = type === 'movie' ? '/movie/now_playing' : '/tv/on_the_air';
  const { data } = await api.get(endpoint);
  return data;
};

// Validate TMDB ID at the boundary
function validateTmdbId(id: number): void {
  if (!Number.isFinite(id) || id <= 0 || !Number.isInteger(id)) {
    throw new Error(`Invalid TMDB ID: ${id}`);
  }
}

// Get Details
export const getMovieDetails = async (id: number): Promise<Movie> => {
  validateTmdbId(id);
  const { data } = await api.get(`/movie/${id}`, {
    params: { append_to_response: 'credits,videos,recommendations,external_ids' },
  });
  return data;
};

export const getTvDetails = async (id: number): Promise<TvShow> => {
  validateTmdbId(id);
  const { data } = await api.get(`/tv/${id}`, {
    params: { append_to_response: 'credits,videos,recommendations,external_ids' },
  });
  return data;
};

export interface PersonExternalIds {
  imdb_id?: string | null;
}

const personExternalIdsCache = new Map<number, Promise<PersonExternalIds>>();

export const getPersonExternalIds = (personId: number): Promise<PersonExternalIds> => {
  validateTmdbId(personId);

  const cached = personExternalIdsCache.get(personId);
  if (cached) return cached;

  const request = api
    .get(`/person/${personId}/external_ids`)
    .then(({ data }) => data as PersonExternalIds)
    .catch((error) => {
      personExternalIdsCache.delete(personId);
      throw error;
    });

  personExternalIdsCache.set(personId, request);
  return request;
};

// Get Season Details
export const getSeasonDetails = async (tvId: number, seasonNumber: number): Promise<Season> => {
  const { data } = await api.get(`/tv/${tvId}/season/${seasonNumber}`);
  return data;
};

// Search
export const searchMulti = async (
  query: string,
  page: number = 1
): Promise<MediaResponse<SearchResult>> => {
  const trimmed = query.trim();
  if (!trimmed) {
    return { page: 1, results: [], total_pages: 0, total_results: 0 };
  }
  const { data } = await api.get('/search/multi', {
    params: { query: trimmed, page, include_adult: false },
  });
  return data;
};

export const searchMovies = async (
  query: string,
  page: number = 1
): Promise<MediaResponse<Movie>> => {
  const { data } = await api.get('/search/movie', {
    params: { query, page, include_adult: false },
  });
  return data;
};

export const searchTvShows = async (
  query: string,
  page: number = 1
): Promise<MediaResponse<TvShow>> => {
  const { data } = await api.get('/search/tv', {
    params: { query, page },
  });
  return data;
};

// Discover by Genre
export const discoverByGenre = async (
  type: 'movie' | 'tv',
  genreIds: number[],
  page: number = 1
): Promise<MediaResponse<Movie | TvShow>> => {
  const { data } = await api.get(`/discover/${type}`, {
    params: {
      with_genres: genreIds.join(','),
      page,
      sort_by: 'popularity.desc',
    },
  });
  return data;
};

export const discoverByProvider = async (
  type: 'movie' | 'tv',
  providerId: number,
  region: string = 'US',
  page: number = 1
): Promise<MediaResponse<Movie | TvShow>> => {
  const { data } = await api.get(`/discover/${type}`, {
    params: {
      watch_region: region,
      with_watch_providers: providerId,
      with_watch_monetization_types: 'flatrate',
      sort_by: 'popularity.desc',
      'vote_count.gte': 25,
      page,
    },
  });
  return data;
};

export type AnimeCatalog = 'popular' | 'airing' | 'top_rated';

// Anime is modeled as a TV catalog body rather than a third-party destination.
// Keep discovery in CineVault; let each title page resolve lawful providers for
// the viewer's region through TMDB's watch-provider data.
export const getAnimeCatalog = async (
  catalog: AnimeCatalog,
  page: number = 1
): Promise<MediaResponse<TvShow>> => {
  const today = new Date().toISOString().slice(0, 10);
  const params: Record<string, string | number | boolean> = {
    with_genres: 16,
    with_origin_country: 'JP',
    with_original_language: 'ja',
    include_adult: false,
    page,
  };

  if (catalog === 'top_rated') {
    params.sort_by = 'vote_average.desc';
    params['vote_count.gte'] = 250;
  } else if (catalog === 'airing') {
    const recentWindow = new Date();
    recentWindow.setUTCDate(recentWindow.getUTCDate() - 45);
    params.sort_by = 'popularity.desc';
    params['air_date.gte'] = recentWindow.toISOString().slice(0, 10);
    params['air_date.lte'] = today;
  } else {
    params.sort_by = 'popularity.desc';
    params['vote_count.gte'] = 50;
  }

  const { data } = await api.get('/discover/tv', { params });
  return data;
};

export interface WatchProvider {
  provider_id: number;
  provider_name: string;
  logo_path: string;
  display_priority: number;
}

export interface WatchProviderRegion {
  link: string;
  flatrate?: WatchProvider[];
  free?: WatchProvider[];
  ads?: WatchProvider[];
  rent?: WatchProvider[];
  buy?: WatchProvider[];
}

export interface WatchProviderResponse {
  id: number;
  results: Record<string, WatchProviderRegion>;
}

export const getWatchProviders = async (
  type: 'movie' | 'tv',
  id: number
): Promise<WatchProviderResponse> => {
  validateTmdbId(id);
  const { data } = await api.get(`/${type}/${id}/watch/providers`);
  return data;
};

// Discover by Mood (maps mood to genres)
export const discoverByMood = async (
  genreIds: number[],
  type: 'movie' | 'tv' = 'movie'
): Promise<MediaResponse<Movie | TvShow>> => {
  return discoverByGenre(type, genreIds, 1);
};

// Get Genres
export const getGenres = async (type: 'movie' | 'tv'): Promise<Genre[]> => {
  const { data } = await api.get(`/genre/${type}/list`);
  return data.genres;
};

// Get Recommendations
export const getRecommendations = async (
  type: 'movie' | 'tv',
  id: number
): Promise<MediaResponse<Movie | TvShow>> => {
  const { data } = await api.get(`/${type}/${id}/recommendations`);
  return data;
};

// Get Similar
export const getSimilar = async (
  type: 'movie' | 'tv',
  id: number
): Promise<MediaResponse<Movie | TvShow>> => {
  const { data } = await api.get(`/${type}/${id}/similar`);
  return data;
};

// Random pick from trending (for hero banner)
export const getRandomTrending = async (): Promise<Movie | TvShow> => {
  const { results } = await getTrending('all', 'week');
  const randomIndex = Math.floor(Math.random() * Math.min(results.length, 10));
  return results[randomIndex];
};

// Get Movie Collection (for sequels/prequels)
export interface MovieCollection {
  id: number;
  name: string;
  overview: string;
  poster_path: string | null;
  backdrop_path: string | null;
  parts: Movie[];
}

export const getMovieCollection = async (collectionId: number): Promise<MovieCollection> => {
  const { data } = await api.get(`/collection/${collectionId}`);
  return data;
};

// Get sequel or next movie in collection
export const getMovieSequel = async (movieId: number): Promise<Movie | null> => {
  try {
    const movie = await getMovieDetails(movieId);

    // Check if movie belongs to a collection
    if (movie.belongs_to_collection?.id) {
      const collection = await getMovieCollection(movie.belongs_to_collection.id);

      // Sort by release date
      const sortedParts = collection.parts.sort((a, b) => {
        const dateA = new Date(a.release_date || '9999').getTime();
        const dateB = new Date(b.release_date || '9999').getTime();
        return dateA - dateB;
      });

      // Find current movie index and return next one
      const currentIndex = sortedParts.findIndex(m => m.id === movieId);
      if (currentIndex !== -1 && currentIndex < sortedParts.length - 1) {
        return sortedParts[currentIndex + 1];
      }
    }

    return null;
  } catch (error) {
    console.error('Error getting movie sequel:', error);
    return null;
  }
};

// Get prequel or previous movie in collection
export const getMoviePrequel = async (movieId: number): Promise<Movie | null> => {
  try {
    const movie = await getMovieDetails(movieId);

    if (movie.belongs_to_collection?.id) {
      const collection = await getMovieCollection(movie.belongs_to_collection.id);

      const sortedParts = collection.parts.sort((a, b) => {
        const dateA = new Date(a.release_date || '9999').getTime();
        const dateB = new Date(b.release_date || '9999').getTime();
        return dateA - dateB;
      });

      const currentIndex = sortedParts.findIndex(m => m.id === movieId);
      if (currentIndex > 0) {
        return sortedParts[currentIndex - 1];
      }
    }

    return null;
  } catch (error) {
    console.error('Error getting movie prequel:', error);
    return null;
  }
};

// Easter egg: Get a random highly-rated movie for "I'm feeling lucky"
export const getRandomGem = async (): Promise<Movie> => {
  const randomPage = Math.floor(Math.random() * 10) + 1;
  const { results } = await api.get('/discover/movie', {
    params: {
      sort_by: 'vote_average.desc',
      'vote_count.gte': 1000,
      'vote_average.gte': 8,
      page: randomPage,
    },
  }).then(res => res.data);

  const randomIndex = Math.floor(Math.random() * results.length);
  return results[randomIndex];
};

// Get next release info for a TV show or movie
export const getNextReleaseInfo = async (
  tmdbId: number,
  mediaType: 'movie' | 'tv'
): Promise<NextReleaseInfo | null> => {
  try {
    if (mediaType === 'tv') {
      const { data } = await api.get(`/tv/${tmdbId}`);

      // Check for next episode to air
      if (data.next_episode_to_air) {
        return {
          type: 'episode',
          date: data.next_episode_to_air.air_date,
          title: `S${data.next_episode_to_air.season_number} E${data.next_episode_to_air.episode_number}`,
          announced: true,
        };
      }

      // If show is still running but no next episode, it's probably between seasons
      if (data.status === 'Returning Series' || data.in_production) {
        return {
          type: 'season',
          date: null,
          title: `Season ${(data.number_of_seasons || 0) + 1}`,
          announced: false,
        };
      }

      // Show ended
      if (data.status === 'Ended' || data.status === 'Canceled') {
        return null;
      }
    } else {
      // For movies, check if there's a sequel in collection
      const { data } = await api.get(`/movie/${tmdbId}`);

      if (data.belongs_to_collection?.id) {
        const collection = await getMovieCollection(data.belongs_to_collection.id);
        const sortedParts = collection.parts.sort((a, b) => {
          const dateA = new Date(a.release_date || '9999').getTime();
          const dateB = new Date(b.release_date || '9999').getTime();
          return dateA - dateB;
        });

        // Find current movie and check for upcoming sequels
        const currentIndex = sortedParts.findIndex(m => m.id === tmdbId);
        if (currentIndex !== -1 && currentIndex < sortedParts.length - 1) {
          const nextMovie = sortedParts[currentIndex + 1];
          const releaseDate = nextMovie.release_date;
          const isFuture = releaseDate && new Date(releaseDate) > new Date();

          if (isFuture || !releaseDate) {
            return {
              type: 'movie',
              date: releaseDate || null,
              title: nextMovie.title,
              announced: !!releaseDate,
            };
          }
        }
      }
    }

    return null;
  } catch (error) {
    console.error('Error getting next release info:', error);
    return null;
  }
};
